/**
 * Result API Endpoints (RTK Query)
 *
 * Uses the centralized baseApi so result summary data is fetched the same way as other modules.
 */

import { baseApi } from '../../../../store/api/baseApi';

export interface ResultSummaryQuery {
	grade: 10 | 12;
	year: number;
}

export interface ResultYearValueDropdownQuery {
	grade: 10 | 12;
}

export interface ResultYearValueDropdownResponse {
	success: boolean;
	data: Array<number | string>;
	message?: string;
}

export interface ResultSummaryResponse {
	success: boolean;
	data: {
		title: string;
		grade: number;
		totalStudents: number;
		totalPassed: number;
		totalCompartment: number;
		totalAbsent: number;
		totalEssentialRepeat: number;
		passPercentage: number;
		performanceIndex: number;
	};
	message?: string;
}

export interface ResultRegionWiseItem {
	region: string;
	totalRegistered: number;
	totalAppeared: number;
	passed: number;
	compartment: number;
	absent: number;
	totalEssentialRepeat: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultRegionWiseResponse {
	success: boolean;
	data: ResultRegionWiseItem[];
	message?: string;
}

export interface ResultGenderWiseItem {
	gender: string;
	totalStudents: number;
	passed: number;
	compartment: number;
	absent: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultGenderWiseResponse {
	success: boolean;
	data: ResultGenderWiseItem[];
	message?: string;
}

export interface ResultCategoryWiseItem {
	socialCategory: string;
	totalStudents: number;
	passed: number;
	compartment: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultCategoryWiseResponse {
	success: boolean;
	data: ResultCategoryWiseItem[];
	message?: string;
}

export interface ResultSponsoredWiseItem {
	sector: string;
	totalStudents: number;
	passed: number;
	compartment: number;
	absent: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultSponsoredWiseResponse {
	success: boolean;
	data: ResultSponsoredWiseItem[];
	message?: string;
}

export interface ResultPmShriWiseItem {
	label: string;
	isPmshri: boolean;
	isPmShri?: boolean;
	totalStudents: number;
	passed: number;
	compartment: number;
	absent: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultPmShriWiseResponse {
	success: boolean;
	data: ResultPmShriWiseItem[];
	message?: string;
}

export interface ResultUrbanRuralWiseItem {
	locationType: string;
	totalStudents: number;
	passed: number;
	compartment: number;
	absent: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultUrbanRuralWiseResponse {
	success: boolean;
	data: ResultUrbanRuralWiseItem[];
	message?: string;
}

export interface ResultStreamWiseItem {
	stream: string;
	streamLabel: string;
	streamName?: string;
	label?: string;
	totalStudents: number;
	passed: number;
	compartment: number;
	absent: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultStreamWiseResponse {
	success: boolean;
	data: ResultStreamWiseItem[];
	message?: string;
}

export interface ResultCwsnWiseItem {
	label: string;
	isCwsn: boolean;
	isCWSN?: boolean;
	cwsn?: boolean;
	totalStudents: number;
	passed: number;
	compartment: number;
	absent: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultCwsnWiseResponse {
	success: boolean;
	data: ResultCwsnWiseItem[];
	message?: string;
}

export interface ResultRegionDetailsItem {
	title: string;
	grade: number;
	region: string;
	nationalRank?: number | null;
	roRank?: number | null;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	cwsnStudents?: number;
	passPercentage: number;
	performanceIndex: number;
	gradeBands?: Record<string, number>;
}

export interface ResultRegionDetailsROItem {
	roName: string;
	sex: string | null;
	socialCategory: string | null;
	urbanRural: string | null;
	cwsn: boolean | null;
	pmshri: boolean | null;
	sector: string | null;
	stream: string | null;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	passPercentage: number;
	performanceIndex: number;
	gradeBands?: Record<string, number>;
}

export interface ResultClassificationSchoolRecord {
	kvCode: number | string;
	schoolName: string;
	sector: string;
	urbanRural: string;
	pmshriStatus: boolean;
	state: string;
	district: string;
	region?: string;
	roRank?: number | null;
	nationalRank?: number | null;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	cwsnStudents?: number;
	passPercentage: number;
	performanceIndex: number;
	gradeBands?: Record<string, number>;
	gender?: Array<{ gender: string; appeared: number; passed: number; compartment: number; passPercentage: number }>;
	socialCategory?: Array<{ socialCategory: string; appeared: number; passed: number; compartment: number; passPercentage: number }>;
	location?: Array<{ locationType: string; appeared: number; passed: number; compartment: number; passPercentage: number }>;
	pmshri?: Array<{ label: string; isPmshri: boolean; appeared: number; passed: number; compartment: number; passPercentage: number }>;
	sponsoredBy?: Array<{ sector: string; appeared: number; passed: number; compartment: number; passPercentage: number }>;
}

export interface ResultRegionDetailsData {
	// Old format (region-level aggregates)
	overAllSummary?: ResultRegionDetailsItem;
	summary?: ResultRegionDetailsItem[];
	// New format (category-filtered region-level aggregates)
	RO?: ResultRegionDetailsROItem[];
	// Legacy format (school-level data with subject summary)
	subjectwiseSummary?: ResultRoSummarySubjectItem[];
	Schools?: ResultClassificationSchoolRecord[];
}

export interface ResultRegionDetailsResponse {
	success: boolean;
	data: ResultRegionDetailsData;
	message?: string;
}

export interface ResultRegionDetailsQuery {
	grade: 10 | 12;
	year: number;
	roName?: string;
	category?: string;
	subcategory?: string;
}

export interface OverallRegionDetailsItem {
	region: string;
	nationalRank: number | null;
	roRank: number | null;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	passPercentage: string;
	performanceIndex: string;
	gradeA1: number;
	gradeA2: number;
	gradeB1: number;
	gradeB2: number;
	gradeC1: number;
	gradeC2: number;
	gradeD1: number;
	gradeD2: number;
	gradeE: number;
}

export interface OverallRegionDetailsData {
	regions: OverallRegionDetailsItem[];
}

export interface OverallRegionDetailsResponse {
	success: boolean;
	data: OverallRegionDetailsData;
	message?: string;
}

export interface OverallRegionDetailsQuery {
	grade: 10 | 12;
	year: number;
}

export interface ResultRoSummaryQuery {
	grade: 10 | 12;
	year: number;
	roName: string;
}

export interface ResultRoSummarySubjectItem {
	subjectCode: number | string;
	subjectName: string;
	appeared: number;
	passed: number;
	failed: number;
	passPercentage: number;
	performanceIndex: number;
	avgMarks: number;
	maxMarks: number;
	minMarks: number;
	gradeBands?: Record<string, number>;
}

export interface ResultRoSummaryData {
	summary: {
		title?: string;
		region: string;
		grade: number;
		totalRegistered: number;
		totalAppeared: number;
		totalPassed: number;
		totalCompartment: number;
		totalAbsent: number;
		totalEssentialRepeat: number;
		passPercentage: number;
		performanceIndex: number;
		gradeBands?: Record<string, number>;
	};
	subjectwiseSummary?: ResultRoSummarySubjectItem[];
	Schools: Array<{
		kvCode: number | string;
		schoolName: string;
		sector: string;
		state: string;
		district: string;
		totalRegistered: number;
		totalAppeared: number;
		totalPassed: number;
		totalCompartment: number;
		totalAbsent: number;
		totalEssentialRepeat: number;
		passPercentage: number;
		performanceIndex: number;
	}>;
}

export interface ResultRoSummaryResponse {
	success: boolean;
	data: ResultRoSummaryData;
	message?: string;
}

export interface ResultRoSubjectStreamSummaryQuery {
	grade: 10 | 12;
	year: number;
	roName: string;
}

export interface ResultRoSubjectStreamItem {
	stream?: string;
	streamLabel?: string;
	subject?: string;
	appearedStudents: number;
	passed?: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultRoSubjectStreamSummaryData {
	roName: string;
	grade: number;
	year: number;
	streams?: ResultRoSubjectStreamItem[];
	subjects?: ResultRoSubjectStreamItem[];
}

export interface ResultRoSubjectStreamSummaryResponse {
	success: boolean;
	data: ResultRoSubjectStreamSummaryData;
	message?: string;
}

export type ResultRoCategoryType =
	| 'sponsoredby'
	| 'gender'
	| 'category'
	| 'urbanrural'
	| 'cwsn'
	| 'pmshri'
	| 'stream';

export interface ResultRoCategorySummaryQuery {
	grade: 10 | 12;
	year: number;
	roName: string;
	category: ResultRoCategoryType;
}

export interface ResultRoCategorySummaryItem {
	label?: string;
	gender?: string;
	category?: string;
	urbanrural?: string;
	cwsn?: string;
	pmshri?: string;
	stream?: string;
	streamLabel?: string;
	sponsoredby?: string;
	totalStudents?: number;
	appearedStudents?: number;
	passed?: number;
	compartment?: number;
	absent?: number;
	passPercentage?: number;
	performanceIndex?: number;
}

export interface ResultRoCategorySummaryData {
	roName: string;
	grade: number;
	year: number;
	category: string;
	data: ResultRoCategorySummaryItem[];
}

export interface ResultRoCategorySummaryResponse {
	success: boolean;
	data: ResultRoCategorySummaryData;
	message?: string;
}

export interface ResultKvDetailsQuery {
	grade: 10 | 12;
	year: number;
	kvCode: number | string;
}

export interface ResultKvDetailsSummary {
	kvCode: number | string;
	schoolName: string;
	region: string;
	state: string;
	district: string;
	sector: string;
	grade: number;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	passPercentage: number;
	performanceIndex: number;
	gradeBands?: Record<string, number>;
}

export interface ResultKvDetailsSubjectItem {
	subject?: string;
	stream?: string;
	streamLabel?: string;
	appearedStudents: number;
	passed?: number;
	passPercentage: number;
	performanceIndex?: number;
	avgMarks?: number;
}

export interface ResultKvDetailsComparisonItem {
	label?: string;
	gender?: string;
	socialCategory?: string;
	sector?: string;
	locationType?: string;
	isCwsn?: boolean;
	isPmshri?: boolean;
	totalRegistered: number;
	totalAppeared?: number;
	passed: number;
	compartment?: number;
	absent?: number;
	totalEssentialRepeat?: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface ResultKvDetailsData {
	summary: ResultKvDetailsSummary;
	subjects: ResultKvDetailsSubjectItem[];
	gender: ResultKvDetailsComparisonItem[];
	category: ResultKvDetailsComparisonItem[];
	streams: ResultKvDetailsSubjectItem[];
	sponsoredBy: ResultKvDetailsComparisonItem[];
	urbanRural: ResultKvDetailsComparisonItem[];
	cwsn: ResultKvDetailsComparisonItem[];
	pmshri: ResultKvDetailsComparisonItem[];
}

export interface ResultKvDetailsResponse {
	success: boolean;
	data: ResultKvDetailsData;
	message?: string;
}

export interface ResultKvStudentResultDetailsQuery {
	grade: 10 | 12;
	year: number;
	kvCode: number | string;
}

export interface ResultKvStudentResultDetailsSubjectItem {
	subjectCode: number | string;
	subjectName: string;
	internalMarks: number;
	practicalMarks: number;
	theoryMarks: number;
	passFail?: string | null;
	grade?: string | null;
}

export interface ResultKvStudentResultDetailsItem {
	studentName: string;
	id: number | string;
	rollNo: number | string;
	fatherName?: string | null;
	motherName?: string | null;
	gender?: string | null;
	socialCategory?: string | null;
	result?: string | null;
	totalMarks?: number | null;
	subjects?: ResultKvStudentResultDetailsSubjectItem[];
}

export interface ResultKvStudentResultDetailsResponse {
	success: boolean;
	students?: ResultKvStudentResultDetailsItem[];
	data?: {
		students?: ResultKvStudentResultDetailsItem[];
	};
	message?: string;
}

export interface ResultStudentResultDetailsQuery {
	grade: 10 | 12;
	year: number;
	rollNo: number | string;
}

export interface ResultStudentResultDetailsItem {
	id: number | string;
	rollNo: number | string;
	registrationNo?: number | string | null;
	studentName: string;
	fatherName?: string | null;
	motherName?: string | null;
	gender?: string | null;
	dob?: string | null;
	socialCategory?: string | null;
	isCwsn?: boolean;
	kvCode: number | string;
	schoolName: string;
	region: string;
	state?: string | null;
	sector?: string | null;
	grade: number;
	year: number;
	result?: string | null;
	totalMarks?: number | null;
	subjects?: ResultKvStudentResultDetailsSubjectItem[];
}

export interface ResultStudentResultDetailsResponse {
	success: boolean;
	data?: ResultStudentResultDetailsItem;
	message?: string;
}

// ── Year Comparison Filter ─────────────────────────────────────────────────────

export type YearComparisonCategory =
	| 'SEX'
	| 'SECTOR'
	| 'URBAN_RURAL'
	| 'PMSHRI'
	| 'SCST'
	| 'CWSN'
	| 'STREAM';

export type YearComparisonLevel = 'RO' | 'KV';

export interface YearComparisonQuery {
	grade: 10 | 12;
	level: YearComparisonLevel;
	category: YearComparisonCategory;
	startingYear: number;
	endYear: number;
	orderby?: string;
}

export interface YearComparisonRecord {
	level: string;
	class: string;
	region: string | null;
	state: string | null;
	district: string | null;
	kvCode: string | null;
	schoolName: string | null;
	sector: string | null;
	urbanRural: string | null;
	isPmshri: boolean | null;
	stream: string | null;
	streamLabel: string | null;
	sex: string | null;
	socialCategory: string | null;
	isCwsn: boolean | null;
	nationalRank: number | null;
	roRank: number | null;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	passPercentage: number;
	performanceIndex: number;
	gradeBands: Record<string, number>;
	year: number;
}

export interface YearComparisonYearData {
	year: number;
	total: number;
	data: YearComparisonRecord[];
}

// ── ──────────────────────────────────────────────────────────────────────────

const extractArrayData = <T>(response: unknown): T[] | null => {
	if (Array.isArray(response)) {
		return response as T[];
	}

	if (!response || typeof response !== 'object') {
		return null;
	}

	const candidate = response as {
		data?: unknown;
		result?: unknown;
		items?: unknown;
	};

	if (Array.isArray(candidate.data)) {
		return candidate.data as T[];
	}

	if (Array.isArray(candidate.result)) {
		return candidate.result as T[];
	}

	if (Array.isArray(candidate.items)) {
		return candidate.items as T[];
	}

	return null;
};

const extractStudentArrayData = (response: unknown): ResultKvStudentResultDetailsItem[] | null => {
	if (Array.isArray(response)) {
		return response as ResultKvStudentResultDetailsItem[];
	}

	if (!response || typeof response !== 'object') {
		return null;
	}

	const candidate = response as ResultKvStudentResultDetailsResponse;

	if (Array.isArray(candidate.students)) {
		return candidate.students;
	}

	if (candidate.data && Array.isArray(candidate.data.students)) {
		return candidate.data.students;
	}

	return null;
};

const extractStudentData = (response: unknown): ResultStudentResultDetailsItem | null => {
	if (!response || typeof response !== 'object') {
		return null;
	}

	const candidate = response as ResultStudentResultDetailsResponse;

	if (candidate.data && typeof candidate.data === 'object') {
		return candidate.data;
	}

	return null;
};

export interface AllKVsItem {
	kvCode: number | string;
	schoolName: string;
	region: string;
	state: string;
	district: string;
	sector: string;
	urbanRural: string;
	pmshriStatus: boolean;
	sex: string | null;
	socialCategory: string | null;
	stream: string | null;
	cwsn: boolean | null;
	nationalRank: number | null;
	roRank: number | null;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	passPercentage: number;
	performanceIndex: number;
	gradeBands?: Record<string, number>;
}

export interface AllKVsData {
	KVs: AllKVsItem[];
}

export interface OverallStateDetailsItem {
	nationalRank: number | null;
	stateName: string;
	sex: string | null;
	socialCategory: string | null;
	urbanRural: string | null;
	cwsn: string | null;
	pmshri: string | null;
	sector: string | null;
	stream: string | null;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	passPercentage: number;
	performanceIndex: number;
	gradeBands?: Record<string, number>;
}

export interface OverallStateDetailsData {
	States: OverallStateDetailsItem[];
}

export interface OverallStateDetailsResponse {
	success: boolean;
	data: OverallStateDetailsData;
	message?: string;
}

export interface OverallStateDetailsQuery {
	grade: 10 | 12;
	year: number;
}

export interface OverallDistrictDetailsItem {
	districtName: string;
	stateName: string;
	region: string;
	nationalRank: number | null;
	roRank: number | null;
	totalRegistered: number;
	totalAppeared: number;
	totalPassed: number;
	totalCompartment: number;
	totalAbsent: number;
	totalEssentialRepeat: number;
	passPercentage: number;
	performanceIndex: number;
}

export interface OverallDistrictDetailsData {
	districts: OverallDistrictDetailsItem[];
}

export interface OverallDistrictDetailsQuery {
	grade: 10 | 12;
	year: number;
}

export interface AllKVsResponse {
	success: boolean;
	data: AllKVsData;
	message?: string;
}

export interface AllKVsQuery {
	grade: 10 | 12;
	year: number;
}

export interface AllKVsDetailsQuery {
	grade: 10 | 12;
	year: number;
	category: string;
	subcategory: string;
}

export interface StateDetailsQuery {
	grade: 10 | 12;
	year: number;
	category: string;
	subcategory: string;
}

export interface DistrictDetailsQuery {
	grade: 10 | 12;
	year: number;
	category: string;
	subcategory: string;
}

// ─── Subject-wise analysis ────────────────────────────────────────────────────

/** Normalised row from /result/all-ro-subject-wise or /result/all-kv-subject-wise */
export interface SubjectWiseItem {
	id: number;
	level: string;
	class: string;
	subjectCode: number;
	subjectName: string;
	roRank: number | null;
	nationalRank: number | null;
	kvCode: number | null;
	kv: string | null;
	ro: string;
	registered: number;
	absent: number;
	appeared: number;
	passed: number;
	failed: number;
	passPercentage: number;
	performanceIndex: number;
	below33: number;
	range33to45: number;
	range45to60: number;
	range60to75: number;
	range75to90: number;
	above90: number;
	gradeA1: number;
	gradeA2: number;
	gradeB1: number;
	gradeB2: number;
	gradeC1: number;
	gradeC2: number;
	gradeD1: number;
	gradeD2: number;
	gradeE: number;
	maxMarks: number;
	minMarks: number;
	avgMarks: number;
}

export interface AllRoSubjectWiseQuery {
	grade: 10 | 12;
	year: number;
}

export interface AllKvSubjectWiseQuery {
	grade: 10 | 12;
	year: number;
	roname?: string;
	level?: 'KVS' | 'KV' | 'RO';
}

const normalizeSubjectWiseItem = (s: Record<string, unknown>): SubjectWiseItem => ({
	id: Number(s['id']) || 0,
	level: (s['LEVEL'] as string) ?? '',
	class: (s['CLASS'] as string) ?? '',
	subjectCode: Number(s['SUB']) || 0,
	subjectName: (s['SNAME'] as string) ?? '',
	roRank: s['RO_RANK'] != null ? Number(s['RO_RANK']) : null,
	nationalRank: s['NATIONAL_RANK'] != null ? Number(s['NATIONAL_RANK']) : null,
	kvCode: s['KVCODE'] != null ? Number(s['KVCODE']) : null,
	kv: (s['KV'] as string | null) ?? null,
	ro: (s['RO'] as string) ?? '',
	registered: Number(s['REG']) || 0,
	absent: Number(s['ABST']) || 0,
	appeared: Number(s['APPD']) || 0,
	passed: Number(s['PASS']) || 0,
	failed: Number(s['FAIL']) || 0,
	passPercentage: Number(s['PASS%']) || 0,
	performanceIndex: Number(s['PI']) || 0,
	below33: Number(s['<33%']) || 0,
	range33to45: Number(s['33-44.9%']) || 0,
	range45to60: Number(s['45-59.9%']) || 0,
	range60to75: Number(s['60-74.9%']) || 0,
	range75to90: Number(s['75-89.9%']) || 0,
	above90: Number(s['>90%']) || 0,
	gradeA1: Number(s['A1']) || 0,
	gradeA2: Number(s['A2']) || 0,
	gradeB1: Number(s['B1']) || 0,
	gradeB2: Number(s['B2']) || 0,
	gradeC1: Number(s['C1']) || 0,
	gradeC2: Number(s['C2']) || 0,
	gradeD1: Number(s['D1']) || 0,
	gradeD2: Number(s['D2']) || 0,
	gradeE: Number(s['E']) || 0,
	maxMarks: Number(s['MAX_MRK']) || 0,
	minMarks: Number(s['MIN_MRK']) || 0,
	avgMarks: Number(s['AVG_MRK']) || 0,
});

export const resultApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getResultYearValueDropdown: build.query<number[] | null, ResultYearValueDropdownQuery>({
			query: ({ grade }) => `/result/yearvaluedropdown?grade=${grade}`,
			transformResponse: (response: ResultYearValueDropdownResponse) => {
				if (!response || !response.success || !Array.isArray(response.data)) {
					return null;
				}

				const years = response.data
					.map((year) => Number(year))
					.filter((year) => Number.isFinite(year));

				return years.length ? Array.from(new Set(years)) : null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultSummary: build.query<ResultSummaryResponse['data'] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/summary?grade=${grade}&year=${year}`,
			transformResponse: (response: ResultSummaryResponse) => {
				if (response && response.success && response.data) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultRegionWise: build.query<ResultRegionWiseItem[] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/regionwise?grade=${grade}&year=${year}`,
			transformResponse: (response: ResultRegionWiseResponse) => {
				if (response && response.success && Array.isArray(response.data)) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultRoSummary: build.query<ResultRoSummaryData | null, ResultRoSummaryQuery>({
			query: ({ grade, year, roName }) =>
				`/result/rosummary?year=${year}&grade=${grade}&roname=${encodeURIComponent(roName)}`,
			transformResponse: (response: ResultRoSummaryResponse) => {
				if (response && response.success && response.data) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultRoSubjectStreamSummary: build.query<
			ResultRoSubjectStreamSummaryData | null,
			ResultRoSubjectStreamSummaryQuery
		>({
			query: ({ grade, year, roName }) =>
				`/result/ro-subject-stream-summary?year=${year}&grade=${grade}&roname=${encodeURIComponent(roName)}`,
			transformResponse: (response: ResultRoSubjectStreamSummaryResponse) => {
				if (response && response.success && response.data) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultRoCategorySummary: build.query<ResultRoCategorySummaryData | null, ResultRoCategorySummaryQuery>({
			query: ({ grade, year, roName, category }) =>
				`/result/ro-category-summary?year=${year}&grade=${grade}&roname=${encodeURIComponent(roName)}&cat=${encodeURIComponent(category)}`,
			transformResponse: (response: ResultRoCategorySummaryResponse) => {
				if (response && response.success && response.data) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultKvDetails: build.query<ResultKvDetailsData | null, ResultKvDetailsQuery>({
			query: ({ grade, year, kvCode }) =>
				`/result/kv-result-details?grade=${grade}&year=${year}&kvcode=${encodeURIComponent(String(kvCode))}`,
			transformResponse: (response: ResultKvDetailsResponse) => {
				if (response && response.success && response.data) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultKvStudentResultDetails: build.query<
			ResultKvStudentResultDetailsItem[] | null,
			ResultKvStudentResultDetailsQuery
		>({
			query: ({ grade, year, kvCode }) =>
				`/result/kv-student-result-details?grade=${grade}&year=${year}&kvcode=${encodeURIComponent(String(kvCode))}`,
			transformResponse: (response: ResultKvStudentResultDetailsResponse | unknown) =>
				extractStudentArrayData(response),
			keepUnusedDataFor: 300,
		}),

		getResultStudentResultDetails: build.query<
			ResultStudentResultDetailsItem | null,
			ResultStudentResultDetailsQuery
		>({
			query: ({ grade, year, rollNo }) =>
				`/result/student-result-details?grade=${grade}&year=${year}&rollno=${encodeURIComponent(String(rollNo))}`,
			transformResponse: (response: ResultStudentResultDetailsResponse | unknown) =>
				extractStudentData(response),
			keepUnusedDataFor: 300,
		}),

		getResultGenderWise: build.query<ResultGenderWiseItem[] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/genderwise?grade=${grade}&year=${year}`,
			transformResponse: (response: ResultGenderWiseResponse) => {
				if (response && response.success && Array.isArray(response.data)) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultCategoryWise: build.query<ResultCategoryWiseItem[] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/categorywise?grade=${grade}&year=${year}`,
			transformResponse: (response: ResultCategoryWiseResponse) => {
				if (response && response.success && Array.isArray(response.data)) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultSponsoredWise: build.query<ResultSponsoredWiseItem[] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/sponsoredwise?grade=${grade}&year=${year}`,
			transformResponse: (response: ResultSponsoredWiseResponse) => {
				if (response && response.success && Array.isArray(response.data)) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultPmShriWise: build.query<ResultPmShriWiseItem[] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/pmshriwise?grade=${grade}&year=${year}`,
			transformResponse: (response: ResultPmShriWiseResponse) => {
				if (response && response.success && Array.isArray(response.data)) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultUrbanRuralWise: build.query<ResultUrbanRuralWiseItem[] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/urbanruralwise?grade=${grade}&year=${year}`,
			transformResponse: (response: ResultUrbanRuralWiseResponse) => {
				if (response && response.success && Array.isArray(response.data)) {
					return response.data;
				}

				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultStreamWise: build.query<ResultStreamWiseItem[] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/streamwise?grade=${grade}&class=${grade}&year=${year}`,
			transformResponse: (response: ResultStreamWiseResponse | unknown) => extractArrayData<ResultStreamWiseItem>(response),
			keepUnusedDataFor: 300,
		}),

		getResultCwsnWise: build.query<ResultCwsnWiseItem[] | null, ResultSummaryQuery>({
			query: ({ grade, year }) => `/result/cwsnwise?grade=${grade}&class=${grade}&year=${year}`,
			transformResponse: (response: ResultCwsnWiseResponse | unknown) => extractArrayData<ResultCwsnWiseItem>(response),
			keepUnusedDataFor: 300,
		}),

		getResultRegionDetails: build.query<ResultRegionDetailsData | null, ResultRegionDetailsQuery>({
			query: ({ grade, year, roName, category, subcategory }) => {
				let url = `/result/regiondetails?grade=${grade}&year=${year}`;
				if (roName)      url += `&roname=${encodeURIComponent(roName)}`;
				if (category)    url += `&category=${encodeURIComponent(category)}`;
				if (subcategory) url += `&subcategory=${encodeURIComponent(subcategory)}`;
				return url;
			},
			transformResponse: (response: ResultRegionDetailsResponse) => {
				if (response && response.success && response.data) {
					return response.data;
				}
				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultOverallRegionDetails: build.query<OverallRegionDetailsData | null, OverallRegionDetailsQuery>({
			query: ({ grade, year }) => `/result/overallregiondetails?grade=${grade}&year=${year}`,
			transformResponse: (response: OverallRegionDetailsResponse) => {
				if (response && response.success && response.data) {
					return response.data;
				}
				return null;
			},
			keepUnusedDataFor: 300,
		}),

		getResultOverallAllKVs: build.query<AllKVsData | null, AllKVsQuery>({
			query: ({ grade, year }) => `/result/overallallkvsdetails?grade=${grade}&year=${year}`,
			transformResponse: (response: unknown) => {
				const r = response as { success?: boolean; data?: Record<string, unknown> };
				if (!r?.success || !r?.data) return null;
				const raw = (r.data['kvs'] ?? r.data['KVs']) as Array<Record<string, unknown>> | undefined;
				if (!Array.isArray(raw)) return null;
				const KVs: AllKVsItem[] = raw.map((kv) => ({
					kvCode: kv['kvCode'] as number | string,
					schoolName: ((kv['kvName'] ?? kv['schoolName']) as string) ?? '',
					region: (kv['region'] as string) ?? '',
					state: (kv['state'] as string) ?? '',
					district: (kv['district'] as string) ?? '',
					sector: (kv['sector'] as string) ?? '',
					urbanRural: (kv['urbanRural'] as string) ?? '',
					pmshriStatus: (kv['pmshriStatus'] as boolean) ?? false,
					sex: (kv['sex'] as string | null) ?? null,
					socialCategory: (kv['socialCategory'] as string | null) ?? null,
					stream: (kv['stream'] as string | null) ?? null,
					cwsn: (kv['cwsn'] as boolean | null) ?? null,
					nationalRank: (kv['nationalRank'] as number | null) ?? null,
					roRank: ((kv['roRank'] ?? kv['rorank']) as number | null) ?? null,
					totalRegistered: Number(kv['totalRegistered']) || 0,
					totalAppeared: Number(kv['totalAppeared']) || 0,
					totalPassed: Number(kv['totalPassed']) || 0,
					totalCompartment: Number(kv['totalCompartment']) || 0,
					totalAbsent: Number(kv['totalAbsent']) || 0,
					totalEssentialRepeat: Number(kv['totalEssentialRepeat']) || 0,
					passPercentage: Number(kv['passPercentage']) || 0,
					performanceIndex: Number(kv['performanceIndex']) || 0,
				}));
				return { KVs };
			},
			keepUnusedDataFor: 300,
		}),

		getResultAllKVsDetails: build.query<AllKVsData | null, AllKVsDetailsQuery>({
			query: ({ grade, year, category, subcategory }) =>
				`/result/allkvsdetails?grade=${grade}&year=${year}&category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(subcategory)}`,
			transformResponse: (response: unknown) => {
				const r = response as { success?: boolean; data?: Record<string, unknown> };
				if (!r?.success || !r?.data) return null;
				const raw = (r.data['kvs'] ?? r.data['KVs']) as Array<Record<string, unknown>> | undefined;
				if (!Array.isArray(raw)) return null;
				const KVs: AllKVsItem[] = raw.map((kv) => ({
					kvCode: kv['kvCode'] as number | string,
					schoolName: ((kv['kvName'] ?? kv['schoolName']) as string) ?? '',
					region: (kv['region'] as string) ?? '',
					state: (kv['state'] as string) ?? '',
					district: (kv['district'] as string) ?? '',
					sector: (kv['sector'] as string) ?? '',
					urbanRural: (kv['urbanRural'] as string) ?? '',
					pmshriStatus: (kv['pmshriStatus'] as boolean) ?? false,
					sex: (kv['sex'] as string | null) ?? null,
					socialCategory: (kv['socialCategory'] as string | null) ?? null,
					stream: (kv['stream'] as string | null) ?? null,
					cwsn: (kv['cwsn'] as boolean | null) ?? null,
					nationalRank: (kv['nationalRank'] as number | null) ?? null,
					roRank: ((kv['roRank'] ?? kv['rorank']) as number | null) ?? null,
					totalRegistered: Number(kv['totalRegistered']) || 0,
					totalAppeared: Number(kv['totalAppeared']) || 0,
					totalPassed: Number(kv['totalPassed']) || 0,
					totalCompartment: Number(kv['totalCompartment']) || 0,
					totalAbsent: Number(kv['totalAbsent']) || 0,
					totalEssentialRepeat: Number(kv['totalEssentialRepeat']) || 0,
					passPercentage: Number(kv['passPercentage']) || 0,
					performanceIndex: Number(kv['performanceIndex']) || 0,
				}));
				return { KVs };
			},
			keepUnusedDataFor: 300,
		}),

		getResultOverallStateDetails: build.query<OverallStateDetailsData | null, OverallStateDetailsQuery>({
			query: ({ grade, year }) => `/result/overallstatedetails?grade=${grade}&year=${year}`,
			transformResponse: (response: unknown) => {
				const r = response as { success?: boolean; data?: Record<string, unknown> };
				if (!r?.success || !r?.data) return null;
				const raw = (r.data['states'] ?? r.data['States']) as Array<Record<string, unknown>> | undefined;
				if (!Array.isArray(raw)) return null;
				const States: OverallStateDetailsItem[] = raw.map((s) => ({
					stateName: ((s['state'] ?? s['stateName']) as string) ?? '',
					nationalRank: (s['nationalRank'] as number | null) ?? null,
					roRank: (s['roRank'] as number | null) ?? null,
					sex: (s['sex'] as string | null) ?? null,
					socialCategory: (s['socialCategory'] as string | null) ?? null,
					urbanRural: (s['urbanRural'] as string | null) ?? null,
					cwsn: (s['cwsn'] as string | null) ?? null,
					pmshri: (s['pmshri'] as string | null) ?? null,
					sector: (s['sector'] as string | null) ?? null,
					stream: (s['stream'] as string | null) ?? null,
					totalRegistered: Number(s['totalRegistered']) || 0,
					totalAppeared: Number(s['totalAppeared']) || 0,
					totalPassed: Number(s['totalPassed']) || 0,
					totalCompartment: Number(s['totalCompartment']) || 0,
					totalAbsent: Number(s['totalAbsent']) || 0,
					totalEssentialRepeat: Number(s['totalEssentialRepeat']) || 0,
					passPercentage: Number(s['passPercentage']) || 0,
					performanceIndex: Number(s['performanceIndex']) || 0,
				}));
				return { States };
			},
			keepUnusedDataFor: 300,
		}),

		getResultStateDetails: build.query<OverallStateDetailsData | null, StateDetailsQuery>({
			query: ({ grade, year, category, subcategory }) =>
				`/result/statedetails?grade=${grade}&year=${year}&category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(subcategory)}`,
			transformResponse: (response: unknown) => {
				const r = response as { success?: boolean; data?: Record<string, unknown> };
				if (!r?.success || !r?.data) return null;
				const raw = (r.data['states'] ?? r.data['States']) as Array<Record<string, unknown>> | undefined;
				if (!Array.isArray(raw)) return null;
				const States: OverallStateDetailsItem[] = raw.map((s) => ({
					stateName: ((s['state'] ?? s['stateName']) as string) ?? '',
					nationalRank: (s['nationalRank'] as number | null) ?? null,
					roRank: (s['roRank'] as number | null) ?? null,
					sex: (s['sex'] as string | null) ?? null,
					socialCategory: (s['socialCategory'] as string | null) ?? null,
					urbanRural: (s['urbanRural'] as string | null) ?? null,
					cwsn: (s['cwsn'] as string | null) ?? null,
					pmshri: (s['pmshri'] as string | null) ?? null,
					sector: (s['sector'] as string | null) ?? null,
					stream: (s['stream'] as string | null) ?? null,
					totalRegistered: Number(s['totalRegistered']) || 0,
					totalAppeared: Number(s['totalAppeared']) || 0,
					totalPassed: Number(s['totalPassed']) || 0,
					totalCompartment: Number(s['totalCompartment']) || 0,
					totalAbsent: Number(s['totalAbsent']) || 0,
					totalEssentialRepeat: Number(s['totalEssentialRepeat']) || 0,
					passPercentage: Number(s['passPercentage']) || 0,
					performanceIndex: Number(s['performanceIndex']) || 0,
				}));
				return { States };
			},
			keepUnusedDataFor: 300,
		}),

		getResultDistrictDetails: build.query<OverallDistrictDetailsData | null, DistrictDetailsQuery>({
			query: ({ grade, year, category, subcategory }) =>
				`/result/districtdetails?grade=${grade}&year=${year}&category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(subcategory)}`,
			transformResponse: (response: unknown) => {
				const r = response as { success?: boolean; data?: Record<string, unknown> };
				if (!r?.success || !r?.data) return null;
				const raw = (r.data['districts'] ?? r.data['Districts']) as Array<Record<string, unknown>> | undefined;
				if (!Array.isArray(raw)) return null;
				const districts: OverallDistrictDetailsItem[] = raw.map((d) => ({
					districtName: ((d['district'] ?? d['districtName']) as string) ?? '',
					stateName: ((d['state'] ?? d['stateName']) as string) ?? '',
					region: (d['region'] as string) ?? '',
					nationalRank: (d['nationalRank'] as number | null) ?? null,
					roRank: (d['roRank'] as number | null) ?? null,
					totalRegistered: Number(d['totalRegistered']) || 0,
					totalAppeared: Number(d['totalAppeared']) || 0,
					totalPassed: Number(d['totalPassed']) || 0,
					totalCompartment: Number(d['totalCompartment']) || 0,
					totalAbsent: Number(d['totalAbsent']) || 0,
					totalEssentialRepeat: Number(d['totalEssentialRepeat']) || 0,
					passPercentage: Number(d['passPercentage']) || 0,
					performanceIndex: Number(d['performanceIndex']) || 0,
				}));
				return { districts };
			},
			keepUnusedDataFor: 300,
		}),

		getResultOverallDistrictDetails: build.query<OverallDistrictDetailsData | null, OverallDistrictDetailsQuery>({
			query: ({ grade, year }) => `/result/overalldistrictdetails?grade=${grade}&year=${year}`,
			transformResponse: (response: unknown) => {
				const r = response as { success?: boolean; data?: Record<string, unknown> };
				if (!r?.success || !r?.data) return null;
				const raw = (r.data['districts'] ?? r.data['Districts']) as Array<Record<string, unknown>> | undefined;
				if (!Array.isArray(raw)) return null;
				const districts: OverallDistrictDetailsItem[] = raw.map((d) => ({
					districtName: ((d['district'] ?? d['districtName']) as string) ?? '',
					stateName: ((d['state'] ?? d['stateName']) as string) ?? '',
					region: (d['region'] as string) ?? '',
					nationalRank: (d['nationalRank'] as number | null) ?? null,
					roRank: (d['roRank'] as number | null) ?? null,
					totalRegistered: Number(d['totalRegistered']) || 0,
					totalAppeared: Number(d['totalAppeared']) || 0,
					totalPassed: Number(d['totalPassed']) || 0,
					totalCompartment: Number(d['totalCompartment']) || 0,
					totalAbsent: Number(d['totalAbsent']) || 0,
					totalEssentialRepeat: Number(d['totalEssentialRepeat']) || 0,
					passPercentage: Number(d['passPercentage']) || 0,
					performanceIndex: Number(d['performanceIndex']) || 0,
				}));
				return { districts };
			},
			keepUnusedDataFor: 300,
		}),

		getResultAllRoSubjectWise: build.query<SubjectWiseItem[] | null, AllRoSubjectWiseQuery>({
			query: ({ grade, year }) => `/result/all-ro-subject-wise?grade=${grade}&year=${year}`,
			transformResponse: (response: unknown) => {
				const raw = extractArrayData<Record<string, unknown>>(response);
				if (!raw) return null;
				return raw.map(normalizeSubjectWiseItem);
			},
			keepUnusedDataFor: 300,
		}),

		getResultAllKvSubjectWise: build.query<SubjectWiseItem[] | null, AllKvSubjectWiseQuery>({
			query: ({ grade, year, roname, level }) => {
				let url = `/result/all-kv-subject-wise?year=${year}&grade=${grade}`;
				if (roname) url += `&roname=${encodeURIComponent(roname)}`;
				if (level) url += `&level=${level}`;
				return url;
			},
			transformResponse: (response: unknown) => {
				const raw = extractArrayData<Record<string, unknown>>(response);
				if (!raw) return null;
				return raw.map(normalizeSubjectWiseItem);
			},
			keepUnusedDataFor: 300,
		}),

		getYearComparison: build.query<YearComparisonYearData[] | null, YearComparisonQuery>({
			query: ({ grade, level, category, startingYear, endYear, orderby = 'NATIONAL_RANK' }) =>
				`/result/year-comparison-filter?grade=${grade}&level=${level}&orderby=${orderby}&category=${category}&starting_year=${startingYear}&end_year=${endYear}`,
			transformResponse: (response: unknown) => {
				const r = response as { success?: boolean; data?: { yearsData?: unknown } };
				if (!r?.success || !r?.data) return null;
				const yearsData = r.data.yearsData;
				if (!Array.isArray(yearsData)) return null;
				return yearsData as YearComparisonYearData[];
			},
			keepUnusedDataFor: 300,
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetResultYearValueDropdownQuery,
	useGetResultSummaryQuery,
	useGetResultRegionWiseQuery,
	useGetResultRoSummaryQuery,
	useGetResultRoSubjectStreamSummaryQuery,
	useGetResultRoCategorySummaryQuery,
	useGetResultKvDetailsQuery,
	useGetResultKvStudentResultDetailsQuery,
	useGetResultStudentResultDetailsQuery,
	useGetResultGenderWiseQuery,
	useGetResultCategoryWiseQuery,
	useGetResultSponsoredWiseQuery,
	useGetResultPmShriWiseQuery,
	useGetResultUrbanRuralWiseQuery,
	useGetResultStreamWiseQuery,
	useGetResultCwsnWiseQuery,
	useGetResultRegionDetailsQuery,
	useGetResultOverallRegionDetailsQuery,
	useGetResultOverallAllKVsQuery,
	useGetResultAllKVsDetailsQuery,
	useGetResultOverallStateDetailsQuery,
	useGetResultStateDetailsQuery,
	useGetResultDistrictDetailsQuery,
	useGetResultOverallDistrictDetailsQuery,
	useGetResultAllRoSubjectWiseQuery,
	useGetResultAllKvSubjectWiseQuery,
	useGetYearComparisonQuery,
} = resultApi;
